// src/App.jsx
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import LoginScreen from "./components/Auth/LoginScreen";
import SignupScreen from "./components/Auth/SignupScreen";
import ChatScreen from "./components/Chat/ChatScreen";
import ContactList from "./components/Chat/ContactList";
import Header from "./components/Header";
import AdminPanel from "./components/Admin/AdminPanel";
import AudioCall from "./components/Call/AudioCall";
import VideoCall from "./components/Call/VideoCall";
import { socket, joinRoom, leaveRoom } from "./socket/signalingClient";
import "./App.css";

// Wrapper component to read invite code from URL and pass to SignupScreen
function SignupWithInviteWrapper() {
  const { inviteCode } = useParams();
  return <SignupScreen inviteCode={inviteCode} />;
}

function App() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedChat, setSelectedChat] = useState(null);
  const [audioCallOpen, setAudioCallOpen] = useState(false);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [roomUsers, setRoomUsers] = useState([]);

  // Room management
  useEffect(() => {
    if (!currentUser?.uid) return;
    joinRoom("default-room", currentUser.uid);

    socket.on("room-users", setRoomUsers);
    socket.on("user-joined", (user) => setRoomUsers((prev) => [...prev, user]));
    socket.on("user-left", (user) =>
      setRoomUsers((prev) => prev.filter((u) => u.socketId !== user.socketId))
    );

    return () => {
      leaveRoom("default-room", currentUser.uid);
      socket.off("room-users");
      socket.off("user-joined");
      socket.off("user-left");
    };
  }, [currentUser]);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    navigate(`/chat/${chat.chatId}`);
  };

  const handleStartAudioCall = () => {
    if (!selectedChat) return alert("Select chat first");
    setAudioCallOpen(true);
  };

  const handleStartVideoCall = () => {
    if (!selectedChat) return alert("Select chat first");
    setVideoCallOpen(true);
  };

  const handleCloseCall = () => {
    setAudioCallOpen(false);
    setVideoCallOpen(false);
  };

  return (
    <div className="app-container">
      {currentUser && (
        <Header
          selectedChat={selectedChat}
          onStartAudioCall={handleStartAudioCall}
          onStartVideoCall={handleStartVideoCall}
          roomUsers={roomUsers}
          currentUser={currentUser}
        />
      )}

      <div className="chat-body-scroll with-fixed-header">
        <Routes>
          {!currentUser ? (
            <>
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/signup" element={<SignupScreen />} />

              {/* Invite link route */}
              <Route path="/invite/:inviteCode" element={<SignupWithInviteWrapper />} />

              <Route path="*" element={<Navigate to="/login" />} />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <ContactList
                    onSelectChat={handleSelectChat}
                    selectedChat={selectedChat}
                    roomUsers={roomUsers}
                    currentUser={currentUser}
                  />
                }
              />
              <Route
                path="/chat/:chatId"
                element={
                  selectedChat ? (
                    <ChatScreen selectedChat={selectedChat} roomUsers={roomUsers} />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route path="/admin" element={<AdminPanel currentUser={currentUser} />} />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </div>

      {audioCallOpen && (
        <AudioCall roomId="default-room" userId={currentUser.uid} onClose={handleCloseCall} />
      )}
      {videoCallOpen && (
        <VideoCall roomId="default-room" userId={currentUser.uid} onClose={handleCloseCall} />
      )}
    </div>
  );
}

export default App;
